import { Card, Flex, Skeleton } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useRouteLoaderData } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import { usePluginContext } from "~/contexts/plugin-context";
import type { loader } from "~/root";
import { createTheme } from "~/shared/utils/create-theme";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { useAuthModals } from "~/widgets/auth-modals";
import type {
  PluginInstance,
  ValueChainRegistrationPluginProps,
} from "~/widgets/value-chain-registration/types";

export const ValueChainPlugin: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { affiliate, language, authFeatures, siteSdk, cfProxyData } =
    useClientContext();
  const { gtm } = useGtmContext();
  const data = useRouteLoaderData<typeof loader>("root");
  const pluginSettings = data?.pluginIntegrations?.valueChainRegistration;
  const isFtdEnabled = pluginSettings?.ftd_enabled ?? false;
  const standaloneUrl = pluginSettings?.standalone_url ?? "";
  const { close, open } = useAuthModals();
  const registrationData = authFeatures?.registration?.[0];
  const pageDesignSystem = data?.pageDesignSystem;

  const theme = createTheme(language, {
    colors: pageDesignSystem?.colors ?? {},
    fontSize: pageDesignSystem?.font_size ?? {},
    fontWeight: pageDesignSystem?.font_weight ?? {},
    fontFamily: pageDesignSystem?.font_family ?? {},
    lineHeight: pageDesignSystem?.line_height ?? {},
    borderRadius: pageDesignSystem?.border_radius ?? {},
  });

  const { getDirectusImage } = useDirectus();
  const initialParams: ValueChainRegistrationPluginProps = {
    apiUrl:
      pluginSettings?.api_url ?? "https://casino-registration.jack-casino.com",

    referralCode: affiliate,
    onRegister: async (params) => {
      close();
      const { playerID } = params.data;
      const isMobile = window.matchMedia("(max-width: 700px)").matches;
      await siteSdk.setAuthCookie(params.data);
      gtm.signUp({ eventTime: new Date(), playerID });
      if (isFtdEnabled) {
        window.location.href = `/${language}/ftd`;
      } else if (
        isMobile &&
        (params.bonusCode === "marketingjack" || affiliate)
      ) {
        // если введен бонус код marketingjack или есть аффилят, то открываем казино кассу в мобилке
        window.location.href = `/${language}/casino-cashier`;
      } else {
        const { redirectUrl } = await siteSdk.getRoomUrl(
          language,
          "casino/home",
        );

        window.location.href = redirectUrl;
      }
    },
    onGoToLogin: () => {
      // если заполнен VC login открываем его, нет - обычную форму логина
      open(
        data?.pluginIntegrations?.valueChainLogin?.standalone_url
          ? "valueChainLogin"
          : "login",
      );

      console.info("go to login");
    },
    onClose: () => {
      console.info("on close");
      close();
    },
    termsUrl: pluginSettings?.terms_url ?? "",
    privacyUrl: pluginSettings?.privacy_url ?? "",
    locale: language,
    translations: {
      registration: {
        form_title:
          pluginSettings?.translations?.[0]?.form_title ??
          "Register now and get all your bonuses!",
        form_password_input_label:
          pluginSettings?.translations?.[0]?.form_password_input_label ??
          "Password",
        form_promo_code_button_text:
          pluginSettings?.translations?.[0]?.form_promo_code_button_text ??
          "I have a promo code",
        form_promo_code_input_label:
          pluginSettings?.translations?.[0]?.form_promo_code_input_label ??
          "Promo code",
        validation_invalid_email:
          registrationData?.validation_invalid_email ?? "Invalid email address",
        validation_required:
          registrationData?.validation_required ?? "Required field",
        validation_password_max_width:
          registrationData?.validation_password_max_width ??
          "Must be at most 20 characters",
        validation_password_min_width:
          registrationData?.validation_password_min_width ??
          "Must be at least 5 characters",
        form_submit_button_text:
          pluginSettings?.translations?.[0]?.form_submit_button_text ??
          "Create Account",
        form_email_input_label:
          pluginSettings?.translations?.[0]?.form_email_input_label ?? "Email",

        validation_email_max_length:
          registrationData?.validation_email_max_length ??
          "Must be at most 30 characters",
        validation_email_min_length:
          registrationData?.validation_email_min_length ??
          "Must be at least 6 characters",
        validation_password_contain_spaces:
          registrationData?.validation_password_contain_spaces ??
          "Password must not contain spaces",
        validation_terms_and_privacy_confirmation:
          registrationData?.validation_terms_and_privacy_confirmation ??
          "Please confirm that you have read the Terms and Conditions and Privacy Policy",
        validation_password_is_strong:
          registrationData?.validation_password_is_strong ??
          "Must be at least one special character",
        // специальные поля

        form_hide_promo_code_button_text:
          pluginSettings?.translations?.[0]?.form_hide_promo_code_button_text ??
          "No bonus code",
        value_chain_form_privacy_description_text:
          pluginSettings?.translations?.[0]?.form_privacy_description_text ??
          "I am 18 years old and I accept the",
        value_chain_form_privacy_description_label:
          pluginSettings?.translations?.[0]?.form_privacy_description_label ??
          "Terms and Conditions",
        value_chain_form_terms_description_label:
          pluginSettings?.translations?.[0]?.form_terms_description_label ??
          "Privacy Policy",
        value_chain_form_terms_description_text:
          pluginSettings?.translations?.[0]?.form_terms_description_text ??
          "and",

        value_chain_slot_title_text:
          pluginSettings?.translations?.[0]?.slot_title_text ??
          "First Deposit Bonus",
        value_chain_slot_description_text:
          pluginSettings?.translations?.[0]?.slot_description_text ??
          "100% up to 2,000 $ + 150 FS",
        value_chain_slot_image: getDirectusImage(
          pluginSettings?.translations?.[0]?.slot_image?.image?.id ??
            "https://static-microservices.s3.eu-north-1.amazonaws.com/casino-registration-value-chain-dev/latest/box.png",
        ),
      },
    },
    theme,
    isSlotEnabled: pluginSettings?.is_slot_enabled ?? true,
    isStrengthPasswordFeatureEnabled:
      pluginSettings?.is_strength_password_feature_enabled ?? false,

    // проброс isoCode страны пользователя для установки его страны в БО
    isoCode: cfProxyData["cf-ipcountry"],
  };

  const { loadScript, isLoading } = usePluginContext();

  useEffect(() => {
    if (!standaloneUrl) {
      return;
    }

    let instance: undefined | PluginInstance<ValueChainRegistrationPluginProps>;

    const init = async (): Promise<void> => {
      await loadScript(standaloneUrl);

      if (!ref.current) return;

      instance = window?.QWSValueChainRegistration?.mount(
        ref.current,
        initialParams,
      );
    };

    void init();
    return () => {
      instance?.unmount?.();
    };
  }, [ref, initialParams]);

  return isLoading ? (
    <Flex
      height={"100%"}
      flexDirection={"column"}
      justify={"center"}
      align={"center"}
    >
      <Card
        border={{ radius: "card" }}
        variant="default"
        bg="background-tetriary"
        md={{ maxWidth: 525 }}
        minWidth={312}
        maxWidth={440}
        d={{ maxWidth: 525, minWidth: 500 }}
      >
        <Skeleton radius={8} width={"100%"} height={580} />
      </Card>
    </Flex>
  ) : (
    <div ref={ref} id="value-chain-registration" />
  );
};
